import { createContext, useContext, useState, useEffect, useCallback } from 'react';

/**
 * Cart context for gift cards.
 *
 * Each cart item represents one gift card type the buyer wants to offer:
 *   {
 *     id:               local uuid for React keys / removal
 *     services:         Array<{id, name, price, duration, category}>
 *     duoServices:      {[serviceId]: true} — which services have the DUO option
 *     recipient_name:   string
 *     personal_message: string
 *     unit_total:       amount in euros for ONE card (with DUO multipliers applied)
 *     quantity:         number of identical cards to generate (default 1)
 *   }
 *
 * Each card in `quantity` will get its own unique code at checkout.
 * Buyer info (name / email / phone), promo code, etc. are NOT stored per item —
 * they are collected on the /panier checkout page once.
 */

const STORAGE_KEY = 'hilanta_giftcard_cart_v1';

const CartContext = createContext(null);

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Migrate older items (no quantity / no unit_total field)
    return parsed.map((it) => ({
      ...it,
      quantity: it.quantity ?? 1,
      unit_total: it.unit_total ?? it.total ?? 0,
    }));
  } catch {
    return [];
  }
}

function saveToStorage(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch { /* quota / privacy mode */ }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => loadFromStorage());

  useEffect(() => {
    saveToStorage(items);
  }, [items]);

  const addItem = useCallback((item) => {
    setItems((prev) => [
      ...prev,
      {
        ...item,
        id: `cart-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        quantity: item.quantity ?? 1,
        unit_total: item.unit_total ?? item.total ?? 0,
      },
    ]);
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const updateItem = useCallback((id, patch) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  const setQuantity = useCallback((id, qty) => {
    const next = Math.max(1, Math.min(20, Math.floor(qty)));
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, quantity: next } : it)));
  }, []);

  const incrementItem = useCallback((id) => {
    setItems((prev) => prev.map((it) => (
      it.id === id ? { ...it, quantity: Math.min(20, (it.quantity || 1) + 1) } : it
    )));
  }, []);

  const decrementItem = useCallback((id) => {
    setItems((prev) => prev.flatMap((it) => {
      if (it.id !== id) return [it];
      const next = (it.quantity || 1) - 1;
      if (next <= 0) return [];
      return [{ ...it, quantity: next }];
    }));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  // Totals
  const totalAmount = items.reduce((sum, it) => sum + (it.unit_total || 0) * (it.quantity || 1), 0);
  const itemCount = items.reduce((n, it) => n + (it.quantity || 1), 0);

  return (
    <CartContext.Provider value={{
      items, addItem, removeItem, updateItem, setQuantity,
      incrementItem, decrementItem, clearCart, totalAmount, itemCount,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside <CartProvider>');
  return ctx;
}
